import React, { useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, ControlSelect, useChartTheme, maxBy, PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS, shortDay,
} from '../../components/insights/InsightPage';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { EMPTY, formatNumber, plural } from '../../utils/format';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
/** A long weekend starts on a Monday or a Friday. */
const LONG_WEEKEND_DAYS = ['Monday', 'Friday'];

const HolidayPatterns = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [year, setYear] = useState(CURRENT_YEAR);
  const { data, loading, error, reload } = useInsightData(() => insightsService.holidayPatterns(year), [year]);
  const d = data || {};
  const byWeekday = d.byWeekday || [];
  const byMonth = d.byMonth || [];
  const total = byMonth.reduce((s, r) => s + r.holidays, 0);
  const weekdayTotal = byWeekday.reduce((s, r) => s + r.holidays, 0);

  const topDay = weekdayTotal > 0 ? maxBy(byWeekday, 'holidays') : null;
  const topMonth = total > 0 ? maxBy(byMonth, 'holidays') : null;
  const longWeekend = topDay && LONG_WEEKEND_DAYS.includes(topDay.day);

  const leave = `${longWeekend ? 'annual' : 'Annual'} leave most often starts on a ${topDay?.day} (${topDay?.pct}% of holidays)`;
  const copy = topDay && topMonth
    ? `${longWeekend ? 'The long-weekend effect is real: ' : ''}${leave}, and ${topMonth.month} is the most popular month to be off. 🏖️`
    : '';

  const controls = (
    <ControlSelect id="holidayYear" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
      {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
    </ControlSelect>
  );

  return (
    <InsightPage
      title="Holiday patterns"
      description="When people book their annual leave."
      controls={controls}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={total === 0}
      emptyMessage={`No holidays start in ${year}`}
    >
      <StatRow>
        <StatCard xs={4} label="Most popular start day" value={topDay?.day || EMPTY} sub={topDay ? `${topDay.pct}% of holidays` : ''} variant="primary" />
        <StatCard xs={4} label="Most popular month" value={topMonth?.month || EMPTY} sub={topMonth ? plural(topMonth.holidays, 'holiday') : ''} />
        <StatCard xs={4} label="Holidays" value={formatNumber(total)} sub={`Starting in ${year}`} />
      </StatRow>

      <Row className="g-4">
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-light"><h5 className="mb-0">Holidays by start weekday</h5></Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={isMobile ? 260 : 360}>
                <BarChart data={byWeekday} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 20, left: 10, bottom: 20 }}>
                  <CartesianGrid {...t.grid} />
                  <XAxis dataKey="day" {...t.xAxis} {...(isMobile ? { ...PHONE_X_AXIS, tickFormatter: shortDay } : {})} />
                  <YAxis allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
                  <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
                  <Bar dataKey="holidays" name="Holidays">
                    {byWeekday.map((r) => (
                      <Cell key={r.day} fill={r === topDay ? t.colors.emphasis : t.colors.neutral} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-light"><h5 className="mb-0">Holidays by month</h5></Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={isMobile ? 260 : 360}>
                <BarChart data={byMonth} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 20, left: 10, bottom: 40 }}>
                  <CartesianGrid {...t.grid} />
                  {/* Phones: all twelve months as "Jan", still angled so they fit */}
                  <XAxis
                    dataKey="month"
                    {...t.xAxis}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    {...(isMobile ? { interval: 0, height: 40, tick: { ...t.xAxis.tick, fontSize: 11 }, tickFormatter: (m) => m.slice(0, 3) } : {})}
                  />
                  <YAxis allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
                  <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
                  <Bar dataKey="holidays" name="Holidays">
                    {byMonth.map((r) => (
                      <Cell key={r.month} fill={r === topMonth ? t.colors.emphasis : t.colors.neutral} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default HolidayPatterns;
